import {
    cloneDeep, delay, difference, drop, endsWith, eq, filter, find, first, flatMap, forEach, get, hasIn, includes, invoke,
    isArray, isBoolean, isDate, isEmpty, isEqual, isFunction, isNil, isNumber, isObject, isString,
    join, keys, last, map, pickBy, reduce, remove, replace, size, some, split, startsWith, take, toLower, trim, uniq,
} from "lodash-es";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";

const getParseGeneric = (parseMethod, target, path, defaultValue) => invoke({ parseNumber, parseBool, parseDate, parseDateString, parseLuxonDateTime }, parseMethod, get(target, path, null), defaultValue);

export function tryParseNumber(value, callback) {
    let tryVal = parseNumber(value);
    let isValid = !isNaN(tryVal);
    if(isValid && isFunction(callback)) callback(tryVal);
    return isValid;
}

export function parseNumber (value, defaultValue = NaN) {
    return isNumber(value) || (!isNil(value) && !isNaN(value) && !eq(value, '')) ? Number(value) : defaultValue;
}

export const getNumber = (target, path, defaultValue = null) => getParseGeneric("parseNumber", target, path, defaultValue);
export const getNumberFrom = (target, paths, defaultValue = NaN) => parseNumber(getFrom(target, paths, null), defaultValue);

export function isValidDate(value) {
    if(isNullOrEmpty(value)) return false;
    return !isNaN(Date.parse(value)) && isDate(new Date(value));
}

export function parseDate (value, defaultValue = null) {
    let defaultVal = defaultValue === "now" ? new Date() : defaultValue;
    return isValidDate(value) ? new Date(value) : defaultVal;
}

export function parseDateString (value, defaultValue = null, formatString = "iso") {
    let dt = parseLuxonDateTime(value, defaultValue);
    if(isNil(dt) || !dt.isValid) return defaultValue;
    if(formatString === "iso") return dt.toISO();
    let luxFormatString = size(formatString) > 1
        ? replace(replaceAll(replaceAll(formatString, "Y", "y"), "D", "d"), "T", "'T'")
        : formatString;
    return dt.toFormat(luxFormatString);
}

export function parseLuxonDateTime (value, defaultValue=null) {
    let dateValue = parseDate(value, defaultValue);
    return isNil(dateValue) ? null : DateTime.fromJSDate(dateValue);
}

export const getDate = (target, path, defaultValue = null) => getParseGeneric("parseDate", target, path, defaultValue);

export const getDateString = (target, path, defaultValue = null) => getParseGeneric("parseDateString", target, path, defaultValue);

export const getLuxonDateTime = (target, path, defaultValue = null) => getParseGeneric("parseLuxonDateTime", target, path, defaultValue);

export function parseBool (value, defaultValue = false) {
    if (isBoolean(value)) return value;

    if (!isNil(value)) {
        let trueVals = ["true", "yes", "1"];
        let falseVals = ["false", "no", "0"];
        if (includes(trueVals, value.toString().toLowerCase())) return true;
        if (includes(falseVals, value.toString().toLowerCase())) return false;
    }
    return defaultValue;
}

export const getBool = (target, path, defaultValue = false) => getParseGeneric("parseBool", target, path, defaultValue);

export function isNullOrEmpty(value) {
    if(isNil(value)) return true;
    if(isDate(value) || isNumber(value) || isBoolean(value)) return false;
    if(isString(value)) return isEmpty(trim(value));
    return isEmpty(value);
}

export function getIsEqual(target, path, other, pathOnOther=false, defaultValue=null) {
    let targetValue = get(target, path, defaultValue);
    let otherValue = pathOnOther ? get(other, path, defaultValue) : other;
    return isEqual(targetValue, otherValue);
}

export function getIsNil(target, path) {
    return isNil(get(target, path));
}

export function getIsEmpty(target, path) {
    return isEmpty(get(target, path));
}

export function getWithNullCheck(target, path, defaultValue) {
    let val = get(target, path, defaultValue);
    if(isNil(val)) return defaultValue;
    return val;
}

export function getIsNullOrEmpty(target, path) {
    return isNullOrEmpty(get(target, path));
}

export function getFrom(target, paths, defaultValue) {
    if(isString(paths)) return get(target, paths, defaultValue);
    let result = defaultValue;
    forEach(paths, p => {
        result = get(target, p, defaultValue);
        return result === defaultValue;
    });
    return result;
}

export function getFirst(target, path, defaultValue) {
    return get(first(target), path, defaultValue);
}

export function getFirstNumber(target, path, defaultValue) {
    return getNumber(first(target), path, defaultValue);
}

export function parseUnit(value, defaultUnit="px", defaultValue=null, zeroUnits=false) {
    let _val = isNil(value) ? defaultValue : value;
    if(!isNil(_val) && parseInt(_val) === 0 && !zeroUnits) return "0";
    return isNumber(_val) || (isString(_val) && parseInt(_val) == _val)
        ? `${_val}${defaultUnit}`
        : _val;
}

export function updateAll(collection, key, value) {
    forEach(collection, (item,index) => {
        item[key] = isFunction(value)
            ? value(item,index)
            : value;
    });
}

export function replaceAll(target, pattern, replacement) {
    if(pattern === replacement || includes(replacement, pattern) || !includes(target, pattern)) return target;
    let parsedIndexes = [];
    let result = replace(target, pattern, replacement);
    let nextIndex = result.indexOf(pattern);
    while(nextIndex >= 0 && !includes(parsedIndexes, nextIndex)){
        result = replace(result, pattern, replacement);
        parsedIndexes.push(nextIndex);
        nextIndex = result.indexOf(pattern);
    }
    return result;
}

export function reduceMatchingProp(collection, propName, defaultValue=null){
    return (isEmpty(collection) || !hasIn(collection[0], propName) || collection[0][propName] === defaultValue)
        ? defaultValue
        : reduce(collection,
            (currentVal, item) =>
                currentVal === item[propName]
                    ? currentVal
                    : defaultValue,
            collection[0][propName]
        );
}

export function replaceDelimiter(delimitedString, delimiter, replacement) {
    if(delimiter === replacement) return delimitedString;
    return join(split(delimitedString, delimiter), replacement);
}

export function ensureEndsWith (value, ending) {
    return isString(value) ? endsWith(value, ending) ? value : value + ending : ending;
}

export function ensureStartsWith (value, ensureText) {
    return !isString(value) ? ensureText : (!startsWith(value, ensureText) ? ensureText : "") + value;
}

export function hexDecode (hex) {
    var result = "";

    if (isNil(hex)) return result;

    var hexSplit = hex.split("\\");

    for(let index = 1; index < hexSplit.length; index++) {
        var xhex = hexSplit[index];
        if (!xhex) {
            continue;
        }
        var hexInt = xhex.slice(1);
        result += String.fromCharCode(parseInt(hexInt, 16));
    }
    return result;
}

export function hexEncode (input) {
    var result = "";

    if (isNil(input)) return result;

    for (let index=0; index < input.length; index++) {
        let hex = input.charCodeAt(index).toString(16);
        result +=  "\\u" + (("0000"+hex).slice(-4));
    }
    return result;
}

export function base64ToUint8Array(base64) {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; ++i) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

export function base64ToBlob(base64, blobType) {
    const bytes = base64ToUint8Array(base64);
    return new Blob([bytes], { type: blobType });
}

export function base64ToArrayBuffer(base64) {
    var bytes = base64ToUint8Array(base64);
    return bytes.buffer;
}

export function base64ToObjectUrl(base64, contentType) {
    var blob = base64ToBlob(base64, contentType);
    return URL.createObjectURL(blob);
}

export function htmlToObjectUrl(htmlContent) {
    let htmlValue = htmlContent || '';
    var blob = new Blob([htmlValue], { type: 'text/html' });
    return URL.createObjectURL(blob);
}

export function htmlToPlainText(htmlContent) {
    let parser = new DOMParser();
    let htmlDoc = parser.parseFromString(htmlContent, "text/html");
    return htmlDoc.documentElement.innerText;
}

export function listWords(words, conjunction="and") {
    if(!isArray(words)) return "";
    if(words.length === 1) return words[0];
    if(words.length === 2) return `${words[0]} ${conjunction} ${words[1]}`;
    let delimited = join(take(words, words.length-1), ", ");
    return `${delimited} ${conjunction} ${last(words)}`;
}

export function joinParts(parts, delimiter="", excludeEmptyParts=true) {
    if(!excludeEmptyParts) return join(parts, delimiter);
    if(!isArray(parts)) return "";
    let partsVal = parts.slice();
    remove(partsVal, p=>isEmpty(p) || isNil(p));
    return join(partsVal, delimiter);
}

export function search(targetList, searchText, includeKeys=[], excludeKeys=[], matchCase=false) {
    if(!isArray(targetList) || isEmpty(trim(searchText))) return targetList;
    const parse = val => matchCase ? trim(val) : toLower(trim(val));
    let searchVal = parse(searchText);
    return filter(targetList, val => {
        if(isString(val)) return includes(parse(val), searchVal);
        if(isObject(val)) return some(val, (v,k) => (isEmpty(includeKeys) ? !includes(excludeKeys, k) : includes(includeKeys, k)) && includes(parse(v), searchVal));
        return false;
    });
}

export const differs = (arr1, arr2) => isNil(arr1) !== isNil(arr2)
    || (arr1 || []).length !== (arr2 || []).length
    || some(difference(arr1,arr2));

export function parseKeyListValue(value, suffix=null) {
    const applySuffixes = ks => map(ks, a => endsWith(trim(a), suffix) ? trim(a) : trim(a) + suffix);
    let result = !isEmpty(value)
        ? isArray(value)
            ? value
            : isString(value)
                // @ts-ignore
                ? split(trim(value, [","," "]), ",")
                : []
        : [];
    return isEmpty(suffix) ? result : applySuffixes(result);
}

export function mapLookup(items, keyExpr="id", nameExpr="name", keepObjDefinition=false, keepData=false) {
    const mapItem = item => {
        if(keepObjDefinition) return { ...item, [keyExpr]:parseNumber(item[keyExpr], 0) };
        let result = { id: parseNumber(item[keyExpr], 0), name: item[nameExpr] };
        return keepData
            ? { ...result, data: cloneDeep(item) }
            : result;
    }
    return map(items, item => mapItem(item));
}

export function page(items, skip, take) {
    if(!isArray(items) || isEmpty(items)) return { data: [], totalCount: 0 };
    return { data: take(drop(items, skip), take), totalCount: items.length };
}

export function trimSuffix(toTrim, trim) {
    if (!toTrim || !trim) {
        return toTrim;
    }
    const index = toTrim.lastIndexOf(trim);
    if (index === -1 || (index + trim.length) !== toTrim.length) {
    return toTrim;
    }
    return toTrim.substring(0, index);
}

export function trimPrefix(toTrim, trim) {
    if (!toTrim || !trim) {
        return toTrim;
    }
    const index = toTrim.indexOf(trim);
    if (index !== 0) {
    return toTrim;
    }
    return toTrim.substring(trim.length);
}

export const trimLower = value => toLower(trim(value));

export function evalCssObject(cssObj=null) {
    if(isNil(cssObj)) return "";
    let classArray = keys(pickBy(cssObj));
    if(isEmpty(classArray)) return "";
    let fullClassArray = flatMap(classArray, c => split(c, " "));
    return join(uniq(fullClassArray), " ");
}

export function swap(a, b, m="*") {
    if(isArray(a) || isArray(b)) throw "swapValues : This function does not support swapping array values";
    if(typeof a !== typeof b) throw "swapValues : Types for a and b value arguments must match";
    if(isObject(a) && isNil(m)) throw "swapValues : A non-null field name argument must be provided when passing objects for a and b (default is \"*\", in which case the entire object value is swapped)";

    if(isObject(a) && m !== "*")
        [a[m], b[m]] = [b[m], a[m]];
    else
        [a, b] = [b, a];
}

//simple function to wrap delay and return a promise
export const wait = duration => new Promise(resolve => { delay(resolve, duration); });

export function findValue(list, field, predicate, fromIndex=0) {
    let targetObj = find(list, predicate, fromIndex);
    return get(targetObj, field, null);
}

export function fieldValues(list, field, includeNil=false) {
    let result = map(list, field);
    let uniqResult = uniq(result);
    if(!includeNil) remove(uniqResult, isNil);
    return uniqResult;
}

export function getClassAttr(defaultValue, classes) {
    if(isEmpty(classes)) return trim(defaultValue);
    let resultList = [trim(defaultValue)];
    if(isArray(classes))
        resultList.push(...classes);
    else if(isString(classes))
        resultList.push(trim(classes));
    return join(resultList, " ");
}

export const createGuid = () => uuidv4();

export function recursiveFind(list, childListField, predicate) {
    let result = null;
    forEach(list, (item, index) => {
        if(predicate(item, index)) {
            result = item;
            return false;
        }
        result = recursiveFind(item?.[childListField], childListField, predicate);
        return isNil(result);
    });
    return result;
}

export const keyCount = obj => isObject(obj) ? size(keys(obj)) : 0;